import { create } from "zustand";
import { persist } from "zustand/middleware";
import { registerAuthHooks } from "@/lib/api/client";

export type UserRole =
  | "admin"
  | "manager"
  | "cashier"
  | "warehouse"
  | "sales"
  | "technician"
  | "viewer";

/**
 * roleAllows — luật vai trò TẬP TRUNG (khớp BE auth.RequireRole):
 *   - sales ("Bán hàng + Nhập kho"): đi được mọi nơi cashier HOẶC warehouse đi được.
 *   - viewer ("Chỉ xem"): vào xem được mọi trang KHÔNG phải admin-only;
 *     mọi thao tác ghi bị BE chặn (403).
 * Dùng cho cả route guard lẫn lọc menu — đừng tự viết roles.includes ở nơi khác.
 */
export function roleAllows(userRole: UserRole, allowed?: readonly UserRole[]): boolean {
  if (!allowed || allowed.length === 0) return true;
  if (allowed.includes(userRole)) return true;
  if (userRole === "sales") {
    return allowed.includes("cashier") || allowed.includes("warehouse");
  }
  if (userRole === "viewer") {
    return allowed.some((r) => r !== "admin");
  }
  return false;
}

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  role: UserRole;
  isActive?: boolean;
  mustChangePassword?: boolean;
  /** Được phép sửa đơn giá / chiết khấu khi bán (cờ trong Quản trị → Nhân viên). */
  canEditPrice?: boolean;
  /** Được phép sửa/xóa/hủy phiếu (đơn hàng, phiếu nhập...) (cờ trong Quản trị → Nhân viên). */
  canManageOrders?: boolean;
}

export type AuthStatus = "idle" | "authed" | "refreshing";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  status: AuthStatus;
}

interface AuthActions {
  setSession: (p: { accessToken: string; refreshToken: string; user: AuthUser }) => void;
  setTokens: (p: { accessToken: string; refreshToken: string }) => void;
  clear: () => void;
  setUser: (u: AuthUser) => void;
  setStatus: (s: AuthStatus) => void;
}

const initial: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  status: "idle",
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initial,
      setSession: ({ accessToken, refreshToken, user }) =>
        set({ accessToken, refreshToken, user, status: "authed" }),
      setTokens: ({ accessToken, refreshToken }) =>
        set({ accessToken, refreshToken, status: "authed" }),
      clear: () => set({ ...initial }),
      setUser: (user) => set({ user }),
      setStatus: (status) => set({ status }),
    }),
    {
      name: "nm_auth",
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        status: s.status,
      }),
    },
  ),
);

// Cross-tab sync: when another tab writes to the persisted key, rehydrate
// this tab's in-memory store so guards/login pages observe the new session.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "nm_auth") {
      void useAuthStore.persist.rehydrate();
    }
  });
}

// Register hooks with the API client so it can read/write tokens
// without a circular import at module evaluation time.
registerAuthHooks({
  getTokens: () => {
    const s = useAuthStore.getState();
    return { accessToken: s.accessToken, refreshToken: s.refreshToken };
  },
  setTokens: (p) => useAuthStore.getState().setTokens(p),
  clear: () => useAuthStore.getState().clear(),
  setStatus: (s) => useAuthStore.getState().setStatus(s),
});
